import { ViewDefinition } from "../models/Field";
import { FieldValues } from "../models/FieldValues";
import { FlatVsaaTag } from "../models/VsaaTag";

export interface ComplianceTagGroup {
  documentType: string;
  documentTypeNodeId: string;
  topNodes: ComplianceTopNode[];
}

export interface ComplianceTopNode {
  id: string;
  title: string;
}

const getFieldNamesWithValues = (fieldValues: FieldValues) => {
  const fieldNames = new Set<string>();

  Object.entries(fieldValues.tags).forEach(([fieldName, value]) => {
    if (value && value.length > 0) {
      fieldNames.add(fieldName);
    }
  });

  Object.entries(fieldValues.booleans).forEach(([fieldName, value]) => {
    if (value !== undefined && value !== null) {
      fieldNames.add(fieldName);
    }
  });

  Object.entries(fieldValues.users).forEach(([fieldName, value]) => {
    if (value && value.length > 0) {
      fieldNames.add(fieldName);
    }
  });

  Object.entries(fieldValues.urls).forEach(([fieldName, value]) => {
    if (value) {
      fieldNames.add(fieldName);
    }
  });

  return fieldNames;
};

export const getPublishedActivityFieldNames = (
  fieldValues: FieldValues | undefined,
  viewDefinition: ViewDefinition | undefined,
  excludedFieldNames: string[] = []
) => {
  if (!fieldValues) {
    return [];
  }

  const excludedFieldNameSet = new Set(excludedFieldNames);
  const fieldNamesWithValues = getFieldNamesWithValues(fieldValues);

  const orderedFieldNames =
    viewDefinition?.publishedFieldOrder.filter(
      (fieldName) =>
        fieldNamesWithValues.has(fieldName) &&
        !excludedFieldNameSet.has(fieldName)
    ) ?? [];

  const remainingFieldNames = Array.from(fieldNamesWithValues).filter(
    (fieldName) =>
      !excludedFieldNameSet.has(fieldName) &&
      !orderedFieldNames.includes(fieldName)
  );

  return [...orderedFieldNames, ...remainingFieldNames];
};

const getComplianceDocumentType = (tag: FlatVsaaTag) => {
  const [documentType] = getCompliancePathSegments(tag);

  return documentType || tag.title;
};

const getCompliancePathSegments = (tag: FlatVsaaTag) =>
  tag.path
    ?.split("/")
    .map((segment) => segment.trim())
    .filter(Boolean) ?? [];

const getComplianceTopNode = (tag: FlatVsaaTag): ComplianceTopNode => {
  const pathSegments = getCompliancePathSegments(tag);

  if (pathSegments.length <= 1) {
    return {
      id: tag.id,
      title: tag.title,
    };
  }

  if (pathSegments.length === 2) {
    return {
      id: tag.id,
      title: pathSegments[1],
    };
  }

  return {
    id: tag.ancestorIds?.[2] ?? tag.id,
    title: pathSegments[1],
  };
};

const getComplianceDocumentTypeNodeId = (tag: FlatVsaaTag) => {
  const pathSegments = getCompliancePathSegments(tag);

  if (pathSegments.length <= 1) {
    return tag.id;
  }

  return tag.ancestorIds?.[1] ?? tag.id;
};

export const groupComplianceTagsByDocumentType = (tags: FlatVsaaTag[]) => {
  const groupedTags = new Map<
    string,
    { documentTypeNodeId: string; topNodes: ComplianceTopNode[] }
  >();

  tags.forEach((tag) => {
    const documentType = getComplianceDocumentType(tag);
    const existingGroup = groupedTags.get(documentType);
    const existingTopNodes = existingGroup?.topNodes ?? [];
    const topNode = getComplianceTopNode(tag);

    if (
      existingTopNodes.some((existingNode) => existingNode.id === topNode.id)
    ) {
      return;
    }

    groupedTags.set(documentType, {
      documentTypeNodeId:
        existingGroup?.documentTypeNodeId ??
        getComplianceDocumentTypeNodeId(tag),
      topNodes: [...existingTopNodes, topNode],
    });
  });

  return Array.from(groupedTags.entries()).map(([documentType, group]) => ({
    documentType,
    documentTypeNodeId: group.documentTypeNodeId,
    topNodes: group.topNodes,
  }));
};
